import { ClickAwayListener, debounce, makeStyles, TextField, Theme } from '@material-ui/core'
import { Location } from '@tuqqi/common'
import React, { KeyboardEventHandler, useCallback, useEffect, useRef, useState } from 'react'
import { CSSProperties } from '@material-ui/core/styles/withStyles'
import { useTranslation } from 'react-i18next'
import { initAutocompleteService } from './locationHelpers'
import { inputLocationCache } from './inputLocationCache'
import { SuggestionsLocationPopover } from './SuggestionsLocationPopover'
import { useAddressQueries } from './useAddressQueries'

const styles = (theme: Theme) => ({
    input: {
        marginTop: 1,
        paddingRight: 12,
        paddingLeft: 12,
    },
    rootInput: {
        marginTop: 4,
        borderBottom: '1px solid black',
        background: 'transparent',
        height: 30,
        // borderRadius: 4,
    },
    wrap: {
        position: 'relative'
    } as CSSProperties,
    focused: {
        borderBottom: '1px solid #3f51b5',
        // boxShadow: '0px 0px 4px rgba(123, 79, 240, 0.16)',
    }
})
const useStyles = makeStyles(styles)


interface __Props {
    isNewItem?: boolean
    inputPlaceholder: string
    initialValue: string
    searchOptions: (query: string, setOptions: React.Dispatch<React.SetStateAction<string[]>>) => Promise<void>
    autoFocus?: boolean
    onBlur?(locationAddress?: string): void
}

const _LocationSearchField = (props: __Props) => {
    const { inputPlaceholder, searchOptions, autoFocus, onBlur, initialValue, isNewItem } = props
    const [options, setOptions] = useState<string[]>([])
    const [value, setValue] = useState(initialValue)
    const [isFocused, setIsFocused] = useState(false)
    const popupRef = useRef<HTMLDivElement>(null)
    const classes = useStyles()

    useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    useEffect(() => {
        inputLocationCache.set(value)
    }, [value])

    const getNewOptions = useCallback((value: string) => {
        (async () => {
            if (value) {
                await searchOptions(value, setOptions)
                return
            }
            setOptions([])
        })()
    }, [searchOptions])

    const debouncedSearch = useCallback(debounce(getNewOptions, 400), [getNewOptions])

    useEffect(() => {
        if(!isFocused) return
        debouncedSearch(value)
    }, [isFocused, value, debouncedSearch])

    const _onFocus = () => {
        setIsFocused(true)
    }

    const onChangeText: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setValue(e?.target?.value ?? '')
    }

    const onKeyDown: KeyboardEventHandler = (e) => {}

    const onSelect = (x: string) => {
        setIsFocused(false)
        onBlur?.(x)
    } 
    const onClickAwayNewItem = () => {
        if(!isNewItem) return
        if(!isFocused) return
        setIsFocused(false)
        onBlur?.(value)
    } 

    const Input = <div data-intrcm-id="LocationInput_ork3dl3b" className={`${classes.rootInput} ${isFocused ? classes.focused : ''}`}>
        <TextField 
            onKeyDown={onKeyDown}
            value={value}
            onChange={onChangeText}
            onFocus={_onFocus}
            InputProps={{disableUnderline: true}}
            inputProps={{ className: classes.input }}
            autoFocus={autoFocus}
            placeholder={inputPlaceholder}
            fullWidth />
    </div>

    if(!!isNewItem) return <div data-intrcm-id="LocationInput_7ebnyy0p" className={classes.wrap}>
        <ClickAwayListener onClickAway={onClickAwayNewItem}>
            <div data-intrcm-id="LocationInput_2jnqwdbl">
                {Input}
                <SuggestionsLocationPopover
                    popupRef={popupRef}
                    focused={isFocused}
                    value={value} 
                    onSelect={onSelect}
                    options={options} />
            </div>
        </ClickAwayListener>
    </div>

    return <div data-intrcm-id="LocationInput_kc3n9x2s" className={classes.wrap}>
            <div data-intrcm-id="LocationInput_13yx4nu1">
            {Input}
            <SuggestionsLocationPopover 
                popupRef={popupRef}
                focused={isFocused}
                value={value} 
                onSelect={onSelect}
                options={options} />
            </div>
    </div>
}

interface Props {
    onBlur?(locationAddress?: string): void
    locationAddress: Location | null
    isNewItem?: boolean
}

export const LocationInput = (props: Props) => {
    const { onBlur, locationAddress, isNewItem } = props
    const autocompleteServiceRef = useRef(initAutocompleteService())

    const getAutocompleteService = useCallback(() => autocompleteServiceRef.current, [])
    const { searchAddresses } = useAddressQueries(getAutocompleteService)

    const [tForPlaceholder] = useTranslation("i18_fullview")

    const initialValue = locationAddress?.locationAddress || ''

    const _onBlur = async (locationAddress?: string) => {
        if(!locationAddress) {
            onBlur?.('')
            return
        }

        onBlur?.(locationAddress)
    }

    const inputPlaceHolder = `${tForPlaceholder("Enter address")}...`

    const autoFocus = false

    // eslint-disable-next-line react/jsx-pascal-case
    return <_LocationSearchField 
        onBlur={_onBlur}
        isNewItem={isNewItem}
        initialValue={initialValue}
        inputPlaceholder={inputPlaceHolder}
        searchOptions={searchAddresses}
        autoFocus={autoFocus} />
}